import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import './About.css';

const CHIPS_PER_PAGE = 12;

const FEATURES = [
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M24 4L8 10v14c0 10 7 18 16 20 9-2 16-10 16-20V10z"/>
        <path d="M17 24l5 5 9-9"/>
      </svg>
    ),
    title: 'DVSA-Approved Instructors',
    desc: 'Every instructor holds a current ADI green badge, verified by us.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 6L6 12v28l11-6 14 6 11-6V8l-11 6z"/>
        <line x1="17" y1="6" x2="17" y2="34"/>
        <line x1="31" y1="14" x2="31" y2="42"/>
        <circle cx="11" cy="24" r="2.5" fill="currentColor" stroke="none"/>
        <circle cx="24" cy="30" r="2.5" fill="currentColor" stroke="none"/>
        <circle cx="37" cy="18" r="2.5" fill="currentColor" stroke="none"/>
      </svg>
    ),
    title: 'Nationwide Network',
    desc: 'Independent ADIs across UK — no franchise restrictions.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M7 7h18l16 17-16 17H7z"/>
        <circle cx="14.5" cy="14.5" r="2.5" fill="currentColor" stroke="none"/>
        <path d="M29 21c-1.5-1.5-4.5-1.5-5.5.5s.5 4 2.5 4.5c2.5.5 4.5 2.5 3 5s-4.5 2.5-6 1.5"/>
        <line x1="27" y1="18" x2="27" y2="21"/>
        <line x1="27" y1="32.5" x2="27" y2="36"/>
      </svg>
    ),
    title: 'Transparent Pricing',
    desc: 'All driving lesson packages are clearly priced with no hidden fees, so you know exactly what you’re paying for.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="24" cy="24" r="18"/>
        <circle cx="24" cy="24" r="5"/>
        <line x1="24" y1="6" x2="24" y2="19"/>
        <line x1="8.4" y1="33" x2="19.7" y2="26.5"/>
        <line x1="39.6" y1="33" x2="28.3" y2="26.5"/>
      </svg>
    ),
    title: 'Manual & Automatic',
    desc: 'Train in the car that suits your needs — or try both before committing.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="6" y="10" width="36" height="30" rx="4"/>
        <line x1="6" y1="20" x2="42" y2="20"/>
        <line x1="16" y1="6" x2="16" y2="14"/>
        <line x1="32" y1="6" x2="32" y2="14"/>
        <circle cx="16" cy="28" r="2" fill="currentColor" stroke="none"/>
        <circle cx="24" cy="28" r="2" fill="currentColor" stroke="none"/>
        <circle cx="32" cy="28" r="2" fill="currentColor" stroke="none"/>
        <circle cx="16" cy="35" r="2" fill="currentColor" stroke="none"/>
        <circle cx="24" cy="35" r="2" fill="currentColor" stroke="none"/>
      </svg>
    ),
    title: 'Flexible Scheduling',
    desc: 'Lessons 7 days a week. Your instructor works around your timetable.',
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="10" y="24" width="28" height="20" rx="4"/>
        <path d="M18 24V16a6 6 0 0 1 12 0"/>
        <line x1="30" y1="16" x2="30" y2="8"/>
        <circle cx="24" cy="34" r="3"/>
        <line x1="24" y1="37" x2="24" y2="41"/>
      </svg>
    ),
    title: 'No Franchise Pressure',
    desc: 'Instructors work independently. Authentic, undivided attention every lesson.',
  },
];

function About() {
  const [centreSearch, setCentreSearch] = useState('');
  const [centrePage, setCentrePage] = useState(0);
  const [testCentres, setTestCentres] = useState([]);
  const [siteStats, setSiteStats] = useState({ instructors: null, testCentres: null, students: null });
  const sliderRef = useRef(null);

  useEffect(() => {
    api.get('/test-centres?withInstructors=true')
      .then(data => setTestCentres((data.testCentres || []).map(tc => tc.name)))
      .catch(() => setTestCentres([]));

    // Fetch live stats from backend
    api.get('/stats')
      .then(data => setSiteStats(data))
      .catch(() => {}); // silently fall back to defaults
  }, []);

  const filtered = testCentres.filter(c =>
    c.toLowerCase().includes(centreSearch.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / CHIPS_PER_PAGE);
  const pageChips = filtered.slice(centrePage * CHIPS_PER_PAGE, (centrePage + 1) * CHIPS_PER_PAGE);

  const goTo = (page) => {
    setCentrePage(page);
    sliderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  return (
    <div className="ab-page">

      {/* ─── Hero ─── */}
      <div className="ab-hero">
        <div className="ab-hero-inner">
          <span className="ab-eyebrow">🇬🇧 About Us</span>
          <h1>The UK's Trusted Driving Lesson Network</h1>
          <p>Connecting learner drivers with DVSA-approved independent instructors. No franchise pressure — just great teaching.</p>
        </div>
      </div>

      {/* ─── Stats Bar ─── */}
      <div className="ab-stats-bar">
        <div className="ab-stats-inner">
          {[
            {
              num: siteStats.instructors != null
                ? (() => { const n = siteStats.instructors + 30; return n >= 1000 ? `${(n / 1000).toFixed(1)}k+` : `${n}+`; })()
                : '1,000+',
              label: 'Qualified Instructors'
            },
            {
              num: testCentres.length > 0 ? `${testCentres.length}+` : '20+',
              label: 'Test Centre Locations'
            },
            {
              num: siteStats.students != null
                ? (() => { const n = siteStats.students + 30; return n >= 1000 ? `${(n / 1000).toFixed(1)}k+` : `${n}+`; })()
                : '5,000+',
              label: 'Happy Students'
            },
            { num: '100%', label: 'DVSA Approved' },
          ].map((s, i) => (
            <div className="ab-stat" key={i}>
              <span className="ab-stat-num">{s.num}</span>
              <span className="ab-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Mission ─── */}
      <section className="ab-section ab-section--white">
        <div className="ab-container ab-mission-grid">
          <div className="ab-mission-text">
            <span className="ab-eyebrow-sm">Our Mission</span>
            <h2>Making Driving Lessons Simple, Affordable &amp; Accessible</h2>
            <p>Fast Track Driving School is a UK-wide driving lesson referral platform connecting learner drivers with DVSA-approved independent driving instructors.</p>
            <p>We are not tied to a handful of postcodes. Our growing network of independent ADIs operates across Greater London and beyond, letting students find trusted local instructors wherever they are — without the costs and constraints of big franchise models.</p>
            <p>For instructors, we offer a platform to build and grow their business with complete freedom, backed by a dedicated support team and a steady pipeline of students.</p>
          </div>
          <div className="ab-mission-visual">
            <div className="ab-mission-card">
              <span className="ab-mission-icon">🎯</span>
              <h3>Our Vision</h3>
              <p>To make learning to drive simple and affordable — while giving instructors the freedom to work independently, without franchise constraints or hidden fees.</p>
            </div>
            <div className="ab-mission-card ab-mission-card--red">
              <span className="ab-mission-icon">🚀</span>
              <h3>For Instructors</h3>
              <p>Keep 100% of your lesson earnings. We handle the marketing, compliance checks, and student matching so you can focus on teaching.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Why Choose ─── */}
      <section className="ab-section ab-section--grey">
        <div className="ab-container">
          <div className="ab-section-head">
            <span className="ab-eyebrow-sm">Why Choose Us</span>
            <h2>What Sets Fast Track Apart</h2>
            <p className="ab-section-intro">We've built the platform we wish existed — transparent, fair, and focused on outcomes for both students and instructors.</p>
          </div>
          <div className="ab-features-grid">
            {FEATURES.map((f, i) => (
              <div className="ab-feature-card" key={i}>
                <span className="ab-feature-icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Test Centres ─── */}
      <section className="ab-section ab-section--navy">
        <div className="ab-container">
          <div className="ab-section-head ab-section-head--light">
            <span className="ab-eyebrow-sm ab-eyebrow-sm--light">Coverage</span>
            <h2>Test Centres We Cover</h2>
            <p className="ab-section-intro ab-section-intro--light">Instructors in our network serve the following DVSA test centres and surrounding areas:</p>
          </div>

          {/* Search */}
          <div className="ab-centres-search-wrap">
            <div className="ab-centres-search">
              <svg className="ab-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7"/>
                <line x1="16.5" y1="16.5" x2="22" y2="22"/>
              </svg>
              <input
                type="text"
                placeholder="Search test centres…"
                value={centreSearch}
                onChange={e => { setCentreSearch(e.target.value); setCentrePage(0); }}
                className="ab-centres-input"
              />
              {centreSearch && (
                <button className="ab-search-clear" onClick={() => { setCentreSearch(''); setCentrePage(0); }} aria-label="Clear search">
                  ✕
                </button>
              )}
            </div>
            <span className="ab-centres-count">{filtered.length} centre{filtered.length !== 1 ? 's' : ''} found</span>
          </div>

          {/* Chip grid */}
          <div className="ab-centres-slider" ref={sliderRef}>
            {pageChips.length > 0 ? (
              <div className="ab-centres-grid">
                {pageChips.map((c, i) => (
                  <div className="ab-centre-chip" key={i}>
                    <svg viewBox="0 0 24 24" fill="currentColor" className="ab-chip-pin" aria-hidden="true">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    {c}
                  </div>
                ))}
              </div>
            ) : (
              <p className="ab-centres-empty">No centres match "<strong>{centreSearch}</strong>" — <Link to="/contact" className="ab-centres-link">contact us</Link> to enquire.</p>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ab-centres-pagination">
              <button
                className="ab-page-btn"
                onClick={() => goTo(centrePage - 1)}
                disabled={centrePage === 0}
                aria-label="Previous page"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <div className="ab-page-dots">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    className={`ab-page-dot${i === centrePage ? ' ab-page-dot--active' : ''}`}
                    onClick={() => goTo(i)}
                    aria-label={`Page ${i + 1}`}
                  />
                ))}
              </div>
              <button
                className="ab-page-btn"
                onClick={() => goTo(centrePage + 1)}
                disabled={centrePage === totalPages - 1}
                aria-label="Next page"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          )}

          {!centreSearch && (
            <p className="ab-centres-note">Don't see your area? We're expanding constantly — <Link to="/contact" className="ab-centres-link">contact us</Link> to enquire.</p>
          )}
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="ab-cta-band">
        <div className="ab-container ab-cta-inner">
          <div>
            <h2>Ready to Get Started?</h2>
            <p>Book your first lesson today or apply to join our instructor network.</p>
          </div>
          <div className="ab-cta-btns">
            <Link to="/book-lesson" className="ab-btn ab-btn--white">Book a Lesson</Link>
            <Link to="/become-instructor" className="ab-btn ab-btn--outline">Become an Instructor</Link>
          </div>
        </div>
      </section>

    </div>
  );
}

export default About;
